import { Icon, type IconName } from '@/components/ui/Icon';
import { GearIcon, gearKeyForCharacter } from '@/components/ui/GearIcon';
import { InstallPrompt } from '@/components/ui/InstallPrompt';
import { LinkAccountNag } from '@/components/ui/LinkAccountNag';
import type { Tab } from './Shell';
import { useRun } from '@/store/run';
import { useSession } from '@/store/session';

export function Menu({ onPlay, onGo, onFunctions, onMaps }: {
  onPlay: () => void; onGo: (t: Tab) => void; onFunctions: () => void;
  onMaps: () => void;
}) {
  const user = useSession((s) => s.user);
  const collection = useSession((s) => s.collection);
  const bestDepth = useSession((s) => s.bestDepth);
  const axe = useSession((s) => s.equipment.axe);
  const lamp = useSession((s) => s.equipment.lamp);
  const explorer = useSession((s) => s.characters.explorer);
  const inCave = useRun((s) => s.phase !== 'idle');
  const depth = useRun((s) => s.depth);
  const caveName = useRun((s) => s.caveName);

  const found = (collection?.trash_found ?? 0) + (collection?.treasure_found ?? 0);
  const total = (collection?.trash_total ?? 25) + (collection?.treasure_total ?? 25);
  const maxDepth = lamp?.stats.max_depth_m;
  const cap = maxDepth == null || maxDepth < 0 ? 'endless' : `${maxDepth}m`;

  return (
    <div className="h-full overflow-y-auto px-3 pb-6 pt-4">
      <div className="py-4 text-center">
        <h1 className="font-pixel text-3xl text-coin [text-shadow:0_3px_0_#8A5A0E,0_0_24px_rgba(245,197,66,.4)]">
          SHWETAP
        </h1>
        <p className="mt-2 font-pixel text-[7px] uppercase tracking-[.16em] text-ink-dim">
          Tap · Dig · Discover · Earn
        </p>
        <p className="mt-1.5 text-[10px] tracking-widest text-ink-dim">by ReiwaSakura.Tech</p>
      </div>

      {/* Renders nothing unless this device can actually install — see
          pwa/install.ts. The negative margins align it with the page gutter
          while keeping its own internal padding. */}
      <div className="-mx-3">
        {/* Both render null unless they apply: the nag only for guests with
            something to lose, the install CTA only where an install can
            actually happen. */}
        <LinkAccountNag />
        <InstallPrompt />
      </div>

      <button
        type="button"
        onClick={onPlay}
        className="w-full rounded-sm border border-[rgba(255,220,170,.5)]
                   bg-[linear-gradient(#FFA945,#E0761C)] py-4 font-extrabold text-[#2A1503]
                   shadow-[0_4px_0_-1px_#8C440C] active:translate-y-0.5"
      >
        <span className="flex items-center justify-center gap-2">
          <Icon name="pick" size={18} />
          {inCave ? `Resume at ${depth} m` : `Enter ${caveName}`}
        </span>
      </button>

      {/* Which cave, and the way to every other one.
          Directly under the play button because it is the same decision: the button
          above says where you are going, and this is how you change it. Hidden while
          a run is open — the cave you are standing in is not a choice any more, and
          the map screen is still reachable from Functions. */}
      {!inCave && (
        <button
          type="button"
          onClick={onMaps}
          className="mt-2 flex w-full items-center gap-2 rounded-sm border border-ui-lit
                     bg-[linear-gradient(#3A2718,var(--cave-floor))] px-3 py-2.5 text-left
                     shadow-[0_4px_0_-1px_rgba(0,0,0,.55)] active:translate-y-0.5"
        >
          <Icon name="map" size={16} className="text-torch-warm" />
          <span className="min-w-0 flex-1">
            <span className="block text-[12px] font-bold">Maps</span>
            <span className="block truncate text-[10px] text-ink-dim">
              Digging in {caveName} · find the rest
            </span>
          </span>
          <Icon name="chevron-right" size={14} className="text-ink-dim" />
        </button>
      )}

      <div className="mt-2 grid grid-cols-2 gap-2">
        {([['bag', 'bag', 'Inventory'], ['hero', 'hero', 'Characters'],
           ['shop', 'shop', 'Shop'], ['book', 'book', 'Collection']] as
           [Tab, IconName, string][]).map(([id, icon, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => onGo(id)}
            className="rounded-sm border border-ui-lit bg-[linear-gradient(#3A2718,var(--cave-floor))]
                       py-3 text-sm font-bold shadow-[0_4px_0_-1px_rgba(0,0,0,.55)] active:translate-y-0.5"
          >
            <span className="flex items-center justify-center gap-2">
              <Icon name={icon} size={16} className="text-torch-warm" />
              {label}
            </span>
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={onFunctions}
        className="mt-2 w-full rounded-sm border border-ui-lit
                   bg-[linear-gradient(#3A2718,var(--cave-floor))] py-3 text-sm font-bold
                   shadow-[0_4px_0_-1px_rgba(0,0,0,.55)] active:translate-y-0.5"
      >
        <span className="flex items-center justify-center gap-2">
          <Icon name="functions" size={16} className="text-torch-warm" />
          Reward Hub · Withdraw · Missions · More
        </span>
      </button>

      <section className="mt-3 rounded-md border border-[var(--hairline)] bg-[var(--surface)] p-3.5">
        <h2 className="font-pixel text-[9px] text-torch-core">EXPLORER</h2>
        <div className="mt-2.5 flex items-center gap-3">
          {/* The player's actual character, not a generic glyph. Falls back to the
              drawn hero mark rather than an emoji if the render is missing. */}
          <span className="grid h-13 w-13 shrink-0 place-items-center overflow-hidden rounded-xl
                           border border-ui-lit bg-[linear-gradient(#3B2917,#1D1309)] p-1.5">
            <GearIcon
              gearKey={gearKeyForCharacter(explorer?.key ?? 'jin')}
              size={40}
              alt=""
              fallback={<Icon name="hero" size={22} className="text-torch-warm" />}
            />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate font-extrabold">{user?.display_name ?? 'Explorer'}</p>
            <p className="font-pixel text-[7px] text-ink-dim">LEVEL {user?.level ?? 1}</p>
          </div>
        </div>
      </section>

      <div className="mt-3 grid grid-cols-2 gap-1.5">
        <Stat k="Deepest" v={`${bestDepth} m`} />
        <Stat k="Lamp reaches" v={cap} />
        <Stat k="Axe" v={axe?.name ?? '—'} />
        <Stat k="Collection" v={`${found} / ${total}`} />
      </div>
    </div>
  );
}

function Stat({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between gap-1.5 rounded-lg bg-black/30 px-2.5 py-2 text-[11px]">
      <span className="text-ink-dim">{k}</span>
      <b className="tabular text-ink-hi">{v}</b>
    </div>
  );
}
